import * as Sentry from "@sentry/react";
import { type NavigateOptions, useLocation } from "react-router";
import { useFlowType } from "@lending/hooks/useFlowType";
import { useIsCoApplicantEnabled } from "@lending/hooks/useIsCoApplicantEnabled";
import { useIsPlaidLayerEnabled } from "@lending/hooks/useIsPlaidLayerEnabled";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";
import { useLendingApplicationSteps } from "@lending/hooks/useLendingApplicationSteps";
import {
  type NavigateWithSearchOptions,
  useNavigateWithSearch,
} from "@lending/hooks/useNavigateWithSearch";
import { useProductType } from "@lending/hooks/useProductType";
import { useAutoLoanFlowState } from "@lending/navigation/hooks/useAutoLoanFlowState";
import { usePrefilledOlbSteps } from "@lending/navigation/hooks/usePrefilledOlbSteps";
import { useStore } from "../../contexts/StoreContext";
import {
  type NavigationContext,
  flowConfig,
  getNextStep,
  getPreviousStep,
  getStepPath,
  resolveStepPath,
} from "../flowConfig";
import type { ProductType } from "../types";

interface StartFlowOptions {
  initialStepId?: string;
  navigateOptions?: NavigateOptions;
}

function useFlowNavigation() {
  const navigate = useNavigateWithSearch();
  const location = useLocation();
  const flowType = useFlowType();
  const routeProductType: ProductType | null = useProductType();
  const isCoApplicantEnabled = useIsCoApplicantEnabled();
  const isPlaidLayerEnabled = useIsPlaidLayerEnabled();
  const { showLivingArrangementStep, showEmploymentDetailsStep } = useLendingApplicationSteps();
  const applicationUuid = useLendingApplicationID();
  const { data: storeData, replaceData } = useStore();
  const prefilledOlbSteps = usePrefilledOlbSteps();

  const productType: ProductType | null = storeData.productType ?? routeProductType ?? null;
  const autoLoanFlowState = useAutoLoanFlowState(productType === "auto-loan");

  const buildContext = (override?: Partial<NavigationContext>): NavigationContext => ({
    applicationState: storeData.applicationState ?? undefined,
    isCoApplicantEnabled,
    isPlaidLayerEnabled,
    skippedPlaidPrefill: storeData.skippedPlaidPrefill ?? undefined,
    showLivingArrangementStep,
    showEmploymentDetailsStep,
    hasPrefilledLivingArrangement: prefilledOlbSteps.hasPrefilledLivingArrangement,
    hasPrefilledIncome: prefilledOlbSteps.hasPrefilledIncome,
    hasPrefilledEmployment: prefilledOlbSteps.hasPrefilledEmployment,
    hasCoApplicant: storeData.hasCoApplicant ?? undefined,
    hasSeparateCoApplicantAddress: storeData.hasSeparateCoApplicantAddress ?? undefined,
    hasMultipleCreditCardProducts: storeData.hasMultipleCreditCardProducts ?? undefined,
    hasSelectedCreditCardProduct: storeData.hasSelectedCreditCardProduct ?? undefined,
    hasVehicleSelected:
      productType === "auto-loan"
        ? (autoLoanFlowState.hasVehicleSelected ?? storeData.hasVehicleSelected ?? undefined)
        : (storeData.hasVehicleSelected ?? undefined),
    isRefinance:
      productType === "auto-loan"
        ? (autoLoanFlowState.isRefinance ?? storeData.isRefinanceSelected ?? undefined)
        : undefined,
    isAutoRequestedAmountEnabled:
      productType === "auto-loan" ? autoLoanFlowState.isAutoRequestedAmountEnabled : undefined,
    hasEligibilityCriteria: storeData.hasEligibilityCriteria ?? undefined,
    hasLendingQuestions: storeData.hasLendingQuestions ?? undefined,
    incomeType: storeData.incomeType ?? undefined,
    coApplicantIncomeType: storeData.coApplicantIncomeType ?? undefined,
    ...override,
  });

  // When contextOverride explicitly sets applicationUuid (even to undefined),
  // use that value instead of falling back to the URL-derived applicationUuid.
  // This allows callers like usePlaidLayer to navigate without a UUID after
  // a fresh Plaid verification.
  const resolveUuid = (contextOverride?: Partial<NavigationContext>) =>
    contextOverride && "applicationUuid" in contextOverride
      ? contextOverride.applicationUuid
      : applicationUuid;

  const goNext = (
    contextOverride?: Partial<NavigationContext>,
    navigateOptions?: NavigateOptions
  ) => {
    if (!productType) {
      console.warn("Cannot navigate: product type is unknown");
      Sentry.captureMessage("Flow navigation failed: product type is unknown");
      return;
    }

    const resolvedUuid = resolveUuid(contextOverride);
    const nextPath = getNextStep(
      location.pathname,
      productType,
      flowType,
      buildContext(contextOverride),
      resolvedUuid
    );

    if (nextPath) {
      if (navigateOptions) {
        void navigate(nextPath, navigateOptions);
      } else {
        void navigate(nextPath);
      }
    } else {
      console.warn("Cannot navigate: no next step found");
      Sentry.captureMessage("Flow navigation failed: no next step found");
      startOver();
    }
  };

  const goBack = (contextOverride?: Partial<NavigationContext>) => {
    if (!productType) {
      console.warn("Cannot navigate: product type is unknown");
      Sentry.captureMessage("Flow navigation failed: product type is unknown");
      return;
    }

    const resolvedUuid = resolveUuid(contextOverride);
    // The OLB prefill flags are derived from live data, so completing a step flips its flag
    // and drops it out of the flow — Back would then walk past the step just finished. Back
    // navigates the structural flow: whatever came before, prefilled or not.
    const previousPath = getPreviousStep(
      location.pathname,
      productType,
      flowType,
      buildContext({
        hasPrefilledLivingArrangement: false,
        hasPrefilledIncome: false,
        hasPrefilledEmployment: false,
        ...contextOverride,
      }),
      resolvedUuid
    );

    if (previousPath) {
      void navigate(previousPath);
    } else {
      console.warn("Cannot navigate: no previous step found");
      Sentry.captureMessage("Flow navigation failed: no previous step found");
      startOver();
    }
  };

  const startOver = () => {
    void navigate("/", { replace: true });
  };

  const goToPath = (path: string, options: NavigateWithSearchOptions = {}) => {
    void navigate(path, options);
  };

  const goToStep = (
    stepId: string,
    options: NavigateOptions = {},
    contextOverride?: Partial<NavigationContext>
  ) => {
    if (!productType) {
      console.warn("Cannot navigate: product type is unknown");
      Sentry.captureMessage("Flow navigation failed: product type is unknown");
      return;
    }

    const stepPath = getStepPath(
      stepId,
      productType,
      flowType,
      buildContext(contextOverride),
      applicationUuid
    );

    if (stepPath) {
      void navigate(stepPath, options);
    } else {
      console.warn(`Cannot navigate: step ${stepId} not found`);
      Sentry.captureMessage(`Flow navigation failed: step ${stepId} not found`);
    }
  };

  const startFlow = (
    productType: ProductType,
    contextOverride?: Partial<NavigationContext>,
    options: StartFlowOptions = {}
  ) => {
    replaceData({
      productType,
      hasMultipleCreditCardProducts: contextOverride?.hasMultipleCreditCardProducts,
      hasEligibilityCriteria: contextOverride?.hasEligibilityCriteria,
      hasLendingQuestions: contextOverride?.hasLendingQuestions,
    });

    const context = buildContext(contextOverride);
    const steps = flowConfig[productType]?.[flowType];
    if (!steps || steps.length === 0) {
      console.warn(`Cannot start flow: no steps found for ${productType}`);
      Sentry.captureMessage(`Flow navigation failed: no steps found for ${productType}`);
      return;
    }

    if (options.initialStepId) {
      const initialStepPath = getStepPath(
        options.initialStepId,
        productType,
        flowType,
        context,
        undefined
      );

      if (initialStepPath) {
        void navigate(initialStepPath, options.navigateOptions);
        return;
      }

      console.warn(`Cannot start flow: step ${options.initialStepId} not found`);
      Sentry.captureMessage(
        `Flow navigation failed: step ${options.initialStepId} not found for ${productType}`
      );
      return;
    }

    const firstValidStep = steps.find((step) => !step.condition || step.condition(context));

    if (firstValidStep) {
      void navigate(resolveStepPath(firstValidStep), options.navigateOptions);
    } else {
      console.warn(`Cannot start flow: no valid first step found for ${productType}`);
      Sentry.captureMessage(`Flow navigation failed: no valid first step found for ${productType}`);
    }
  };

  return { goNext, goBack, startOver, goToPath, goToStep, startFlow };
}

export { useFlowNavigation };
